// Web Application Class
// --------------------
// Mirrors the back end or graph call
// Supposed to be a simple storage container
//
// Class imports
import {
  EMPTY_STRING,
  EPOCH_TO_JS_DATE_CONVERSION,
  IN_FLIGHT_TEST_STATUSES,
  isTestLive,
  OPTIMIZTIC_TEST_ID_PREFIX,
  TERMINAL_TEST_STATUSES,
  TEST_STATUS_COMPLETED,
  TEST_STATUS_FAILED,
  TEST_STATUS_INVALID,
  TEST_STATUS_PROVISIONING,
  TEST_STATUS_RUNNING,
  TEST_STATUS_STOPPING,
} from "../constants/definitions";
import {
  eventBus,
  TEST_STATUS_UPDATE,
  LIVE_FINDINGS_UPDATE,
} from "../services/eventBus.service";

// Graph Imports
import {
  fetchLiveFindingsSnapshot,
  getFullTestResultFromGraph,
  getListOfVulns,
} from "../services/dataListOperations.service";
import type { SlimFindingRow } from "../services/dataListOperations.service";
import { subscribeToVulnerabilityDiscovered } from "../services/graphql/customSubscriptions";
import { selectDisplayableFindings } from "../utils/vulnTaxonomy";
import VulnerabilityClass from "./vulnerability";
import VulnerabilityOperationsClass from "./vulnerabilityOperations";

// Lifecycle rank for testStatus. Used to decide whether a (potentially
// stale) getFullTestResult response should be allowed to overwrite the
// in-memory testStatus and its co-traveller fields (endTime, endTrigger,
// testStatusMessage, statistics).
//
// The race we're guarding against: getFullTestResult is dispatched when
// a test is created; a websocket TEST_STATUS_UPDATE then moves the
// instance forward (Running → Stopping → terminal); the late response
// lands carrying the old status captured at request time and would
// otherwise regress the displayed state. By only accepting responses
// whose rank is at or beyond the current rank, we let initial loads
// and same-or-newer updates through, but reject stale ones.
//
//   Provisioning < Running < Stopping < {Completed, Invalid, Failed}
//
// Unknown statuses get rank 0 — treated as "no info", lower than any
// known live state so they don't accidentally beat a real status.
function testStatusRank(status: string | null | undefined): number {
  switch (status) {
    case TEST_STATUS_PROVISIONING: return 1;
    case TEST_STATUS_RUNNING:      return 2;
    case TEST_STATUS_STOPPING:     return 3;
    case TEST_STATUS_COMPLETED:
    case TEST_STATUS_INVALID:
    case TEST_STATUS_FAILED:       return 4;
    default:                       return 0;
  }
}

// Retry policy for hydrateTerminalResult(). The post-terminal fetch is the
// only path that moves a finished test's findings out of the discovery
// stream's session memory and into `vulns`, so a single silently-caught
// failure used to mean the page rendered zero findings for a test the
// backend had recorded in full. Three attempts over ~2.3s covers a dropped
// request and the residual GSI-replication window the backend's own
// read-consistency barrier didn't absorb; beyond that the caller surfaces a
// retry affordance rather than retrying forever behind the user's back.
const TERMINAL_HYDRATION_MAX_ATTEMPTS = 3;
const TERMINAL_HYDRATION_RETRY_BASE_MS = 750;

export interface TestStatistics {
  pagesVisited: number;
  apisObserved: number;
  blockedDeniedDomain: number;
  blockedDeniedPath: number;
  blockedSubdomain: number;
  blockedLogout: number;
  captchaPages: number;
  authenticated: boolean | null;
  // Deterministic reason a test ran unauthenticated (null when authenticated
  // or unknown). Values match the backend AuthenticationReason enum.
  authenticationReason: string | null;
}

interface TestDict {
  id: string;
  owner: string;
  endTime: string;
  endTrigger: string;
  functionCount: number;
  startTime: string;
  startTrigger: string;
  testStatus?: string | null;
  testStatusMessage?: string | null;
  statistics?: TestStatistics | null;
  retestExpiresAt?: number | null;
  isRetestOf?: string | null;
  originalTestDate?: number | null;
  vulns: {
    items?: any[];
    nextToken: string;
  };
  viewed: boolean;
}

interface ReducedDict {
  id: string;
  owner: string;
  startTime: string;
  endTime: string;
  viewed: boolean;
  testStatus?: string | null;
  endTrigger?: string;
}

interface VulnData {
  id: string;
  [key: string]: any;
}

interface SubscriptionMessage {
  onVulnerabilityDiscovered?: SlimFindingRow;
}

// The stored shape on TestResultsClass.liveFindings, consumed by the
// "Findings so far — test in progress" section on the WebApplication page.
//
// dateLastSeen has no counterpart on the subscription payload: the
// VulnerabilityDiscoveryEvent schema type carries dateFirstFound only. It is
// derived locally instead — see the LIVE_FINDING_OBSERVED_AT note at the
// append site in subscribeToVulns(). The reconcile path, reading the same
// row from DynamoDB, does get the authoritative value.
export interface LiveFinding {
  id: string;
  reference: string;
  riskRating: string;
  vulnName: string;
  vulnCategory: string;
  vulnLocation: string;
  pageUrl: string;
  title: string;
  dateFirstFound: number;
  dateLastSeen: number;
}

// Normalize a slim row — from either the discovery subscription or the
// reconcile query — into the stored LiveFinding shape. dateLastSeen is a
// parameter rather than a field read because the two sources supply it
// differently; see the LiveFinding note above.
function toLiveFinding(row: SlimFindingRow, dateLastSeen: number): LiveFinding {
  return {
    id: row.id ?? EMPTY_STRING,
    reference: row.reference ?? EMPTY_STRING,
    riskRating: row.riskRating ?? EMPTY_STRING,
    vulnName: row.vulnName ?? EMPTY_STRING,
    vulnCategory: row.vulnCategory ?? EMPTY_STRING,
    vulnLocation: row.vulnLocation ?? EMPTY_STRING,
    pageUrl: row.pageUrl ?? EMPTY_STRING,
    title: row.title ?? EMPTY_STRING,
    dateFirstFound: row.dateFirstFound ?? 0,
    dateLastSeen,
  };
}

class TestResultsClass {
  id: string;
  owner: string;
  endTime: string;
  endTrigger: string;
  functionCount: number;
  startTime: string;
  startTrigger: string;
  vulns: VulnerabilityClass[];
  vulnsNextToken: string;
  isLive: boolean;
  hasViewedResults: boolean;
  testStatus: string | null;
  testStatusMessage: string | null;
  statistics: TestStatistics | null;
  retestExpiresAt: number | null;
  isRetestOf: string | null;
  originalTestDate: number | null;
  fullResultLoaded: boolean;
  // Has a backend fetch returned this test's findings while the backend
  // itself considered the test TERMINAL? Distinct from fullResultLoaded,
  // which only says "some hydration has happened at some point" — an
  // in-flight hydration says nothing about the final set.
  //
  // This is the flag that authorises dropping the live-stream findings
  // from the displayed set (see getDisplayVulns). Until it is true, the
  // stream's rows are the only complete record this session holds.
  terminalHydrationComplete: boolean;
  subscriptionToVulns: any;
  // True from the moment subscribeToVulns() starts until the underlying
  // subscribe resolves. subscriptionToVulns can only be assigned after that
  // await, so it isn't a sufficient re-entrancy guard on its own: the page's
  // subscription effect re-runs as testSelected is promoted during mount, and
  // a second call landing inside that window would otherwise open a duplicate
  // stream and issue a duplicate catch-up fetch.
  private isSubscribingToVulns: boolean;
  // Does anything actually WANT the discovery stream open? Set when a
  // consumer subscribes, cleared when it unsubscribes — deliberately
  // separate from subscriptionToVulns, which is merely whether a stream
  // exists right now. The pair distinguishes "asked for, and it died"
  // (the resync re-opens it) from "never asked for" (leave it alone, or
  // every webapp with a live run would open a stream nobody reads).
  private vulnStreamDesired: boolean;
  // Disposer for the WebSocket reconnect listener registered alongside the
  // discovery stream. Held so unsubscribeFromVulns can detach it — a
  // listener left registered on the shared client outlives this test.
  private reconnectListenerDisposer: (() => void) | null;
  // Slim live findings streamed in while the test is in-flight. Read together
  // with `vulns` via getDisplayVulns() — see that method for why neither array
  // is a complete picture of an in-flight run on its own.
  liveFindings: LiveFinding[];

  // Constructor
  // ------------
  constructor() {
    this.id = EMPTY_STRING;
    this.owner = EMPTY_STRING;
    this.endTime = EMPTY_STRING;
    this.endTrigger = EMPTY_STRING;
    this.functionCount = 0;
    this.startTime = EMPTY_STRING;
    this.startTrigger = EMPTY_STRING;
    this.vulns = [];
    this.vulnsNextToken = EMPTY_STRING;
    this.isLive = false;
    this.hasViewedResults = false;
    this.testStatus = null;
    this.testStatusMessage = null;
    this.statistics = null;
    this.retestExpiresAt = null;
    this.isRetestOf = null;
    this.originalTestDate = null;

    // Tells us if the test results have been fully loaded
    this.fullResultLoaded = false;
    this.terminalHydrationComplete = false;

    // Vulns subscription
    // If the test is live then subscribe to vulns
    this.subscriptionToVulns = null;
    this.isSubscribingToVulns = false;
    this.vulnStreamDesired = false;
    this.reconnectListenerDisposer = null;
    this.liveFindings = [];
  }

  // Get Full Test Result
  // ---------------------
  // Loads the test record (including all its vulns) from the backend.
  // The getTestResults query returns vulns inline as the first page of the
  // connection, so for tests with <= 100 vulns we can hydrate without any
  // additional network calls. Tests larger than that fall back to the
  // paginated quickListVulnerabilitiess path inside getVulns().
  //
  // IMPORTANT: this is an awaitable operation. Callers can rely on the
  // promise resolving only after `this.vulns` has been populated and
  // `fullResultLoaded` is true. Previous versions returned early while a
  // background fetch was still running, which broke any caller that tried
  // to read vulns immediately after awaiting the call.
  async getFullTestResult(id: string): Promise<void> {
    // The optimiztic placeholder created by webapp.startTest carries a
    // locally-generated id the backend has never seen. Fetching it can only
    // fail, and the failure is indistinguishable from a real one to every
    // caller downstream — so don't make the call.
    if (!id || id.startsWith(OPTIMIZTIC_TEST_ID_PREFIX)) return;

    try {
      const data = await getFullTestResultFromGraph(id);
      if (data) {
        await this.setFullTestResult(data);
      }
    } catch (error) {
      console.error(`Error in getFullTestResult for test ${id}:`, error);
    }
  }

  // Hydrate Terminal Result
  // ------------------------
  // Fetch the FINAL findings for a test that has just reached a terminal
  // status, retrying a bounded number of times, and report whether it
  // worked.
  //
  // Why this exists as its own method rather than a bare getFullTestResult
  // call at the transition: this fetch is the hand-over point between two
  // sources of truth. Before it lands, the session's own record of the run
  // (the discovery stream's liveFindings) is all the FE has; after it lands,
  // the backend is authoritative and getDisplayVulns stops unioning the
  // stream. A silently-swallowed failure here therefore doesn't degrade the
  // view — it empties it, which is how a ServerUnreachable run with findings
  // came to render 0 Critical / 0 High while the backend held 46 records.
  //
  // Returns true only when a response arrived that the backend itself
  // labelled terminal (see setFullTestResult). A response still carrying an
  // in-flight status is a lagging read, not a final answer, so it counts as
  // a failed attempt and is retried.
  async hydrateTerminalResult(): Promise<boolean> {
    if (!this.id || this.id.startsWith(OPTIMIZTIC_TEST_ID_PREFIX)) return false;

    for (let attempt = 0; attempt < TERMINAL_HYDRATION_MAX_ATTEMPTS; attempt++) {
      await this.getFullTestResult(this.id);
      if (this.terminalHydrationComplete) return true;

      const isFinalAttempt = attempt === TERMINAL_HYDRATION_MAX_ATTEMPTS - 1;
      if (!isFinalAttempt) {
        const backoffMs = TERMINAL_HYDRATION_RETRY_BASE_MS * 2 ** attempt;
        await new Promise((resolve) => setTimeout(resolve, backoffMs));
      }
    }

    console.error(
      `Failed to hydrate the final result for test ${this.id} after ` +
        `${TERMINAL_HYDRATION_MAX_ATTEMPTS} attempts; the displayed findings ` +
        `may be incomplete.`,
    );
    return false;
  }

  // Set Test Result
  // ----------------
  // Hydrates this instance from a getTestResults payload. Returns a promise
  // because the multi-page fallback path needs to await getVulns().
  async setFullTestResult(testDict: TestDict): Promise<void> {
    if (!testDict) return;

    // Static / always-overwrite fields. These are immutable after test
    // creation (id, owner, startTime, startTrigger, functionCount) or
    // are concerns of the response payload itself (vulnsNextToken,
    // hasViewedResults, retest fields), so a late response can safely
    // overwrite them.
    this.id = testDict.id;
    this.owner = testDict.owner;
    this.functionCount = testDict.functionCount;
    this.startTime = testDict.startTime;
    this.startTrigger = testDict.startTrigger;
    this.vulnsNextToken = testDict.vulns?.nextToken ?? EMPTY_STRING;
    this.hasViewedResults = testDict.viewed;
    this.retestExpiresAt = testDict.retestExpiresAt ?? null;
    this.isRetestOf = testDict.isRetestOf ?? null;
    this.originalTestDate = testDict.originalTestDate ?? null;

    // Live lifecycle fields — testStatus, endTime, endTrigger,
    // testStatusMessage, statistics. Only overwrite if the response's
    // status is at or beyond the in-memory status in the lifecycle.
    // Without this, a slow getFullTestResult response that captured an
    // old testStatus at request time can land *after* a newer
    // websocket update has moved the test forward, regressing the UI
    // (e.g. flipping a Stopping indicator back to Provisioning). See
    // testStatusRank() above for the ordering.
    const responseStatus = testDict.testStatus ?? null;
    const responseRank = testStatusRank(responseStatus);
    const currentRank  = testStatusRank(this.testStatus);
    if (responseRank >= currentRank) {
      this.testStatus = responseStatus;
      this.endTime = testDict.endTime;
      this.endTrigger = testDict.endTrigger;
      this.testStatusMessage = testDict.testStatusMessage ?? null;
      this.statistics = testDict.statistics ?? null;
    }
    // Recompute isLive from whichever testStatus/endTime we kept.
    // Stopping is treated as live: endTime is set during teardown but
    // the test is still ongoing for UI purposes (Start stays disabled etc).
    this.isLive = isTestLive(this.testStatus, this.endTime);

    const inlineItems = testDict.vulns?.items;
    const hasNextPage = !!testDict.vulns?.nextToken;

    if (Array.isArray(inlineItems) && !hasNextPage) {
      // Single-page payload — hydrate directly from the data we already
      // have. This is the hot path for typical test sizes.
      //
      // Merge vs replace: only merge while the test is live. The merge
      // protects subscription-pushed vulns that the BE resolver hasn't
      // yet returned — but that race only exists during a live run.
      // Once the test is terminal, BE is authoritative; replacing
      // clears any stale local-only vulns that were retained from a
      // prior session (e.g. cached records that BE has since removed
      // in a dedup cleanup). The previous always-merge version was
      // why a BE-side dedup didn't actually reduce the count visible
      // on the webapp page.
      const sorter = new VulnerabilityOperationsClass();
      const hydrated = inlineItems.map((vulnDict) => {
        const vuln = new VulnerabilityClass();
        vuln.setVulnerability(vulnDict);
        return vuln;
      });
      let nextVulns: VulnerabilityClass[];
      if (this.isLive) {
        const incomingIds = new Set(hydrated.map((v) => v.id));
        const localOnly = this.vulns.filter((v) => v.id && !incomingIds.has(v.id));
        nextVulns = [...hydrated, ...localOnly];
      } else {
        nextVulns = hydrated;
      }
      this.vulns = sorter.sortHighestRisk(nextVulns);
    } else {
      // Multi-page or missing inline payload — fall back to paginated load.
      await this.getVulns();
    }

    this.fullResultLoaded = true;

    // Authorise the live-stream hand-over only when the RESPONSE said the
    // test was terminal — not when the local instance happens to be. The
    // rank guard above deliberately keeps a newer local status over a stale
    // response, and in exactly that case the vulns we just wrote are a
    // mid-run snapshot. Treating those as final would drop stream rows the
    // backend hadn't returned yet. Erring the other way merely keeps the
    // union on for one more fetch.
    if (responseStatus && TERMINAL_TEST_STATUSES.has(responseStatus)) {
      this.terminalHydrationComplete = true;
    }
  }

  // Set Reduced Test Result
  // ------------------------
  // Sets the id, owner, start/end date, viewed and is live settings for test result
  // This is sufficient to provide information at the single web app page - reduced network calls
  //
  setReducedTestResult(reducedDict: ReducedDict): void {
    this.id = reducedDict.id;
    this.owner = reducedDict.owner;
    this.startTime = reducedDict.startTime;
    this.endTime = reducedDict.endTime;
    // testStatus and endTrigger come from the reduced query for the
    // TestSelector dropdown — without testStatus the FE can't tell
    // terminal tests apart from Stopping (Stopping has endTime too).
    this.testStatus = reducedDict.testStatus ?? null;
    this.endTrigger = reducedDict.endTrigger ?? this.endTrigger;
    this.isLive = isTestLive(this.testStatus, this.endTime);
    this.hasViewedResults = reducedDict.viewed;
  }

  // Create Default
  // ---------------
  // Creates a default TestResultsClass
  // Used when a new test has just been started
  //
  static createDefault(testid: string, owner: string): TestResultsClass {
    const instance = new TestResultsClass();

    instance.id = testid;
    instance.owner = owner;
    //instance.endTime = EMPTY_STRING
    //instance.endTrigger = EMPTY_STRING
    instance.functionCount = 0;
    instance.startTime = Math.floor(Date.now() / 1000).toString();
    instance.startTrigger = "Manual";
    //instance.vulns = []
    //instance.vulnsNextToken = EMPTY_STRING
    instance.isLive = true;
    //instance.hasViewedResults = false
    // fullResultLoaded stays FALSE. It used to be set true here to stop the
    // dashboard preload re-fetching what is, at creation, an empty record —
    // but the flag means "the backend's findings for this test are on this
    // instance", and for a placeholder that is simply untrue. The lie had
    // two consequences: the Findings tiles trusted a derived count of zero
    // instead of falling back to the stored aggregate, and every later
    // "hydrate if not loaded" path skipped this test for the rest of the
    // session. Fetches against the placeholder's local id are cheap to
    // avoid properly — getFullTestResult skips ids carrying the optimiztic
    // prefix — so the flag no longer has to carry that job.

    return instance;
  }

  /* -------------- */
  /*  List Getters  */
  /* -------------- */

  // Get Vulns
  // ----------
  // Note a test may have no vulns, in which case an empty return is legitimate
  //
  async getVulns(): Promise<void> {
    if (this.id) {
      var vulns: any[] = [];
      // Get the vulns
      await Promise.all([getListOfVulns(this.id)]).then((values) => {
        vulns = values[0].slice();
      });
      // Merge vs replace mirrors setFullTestResult's single-page hot
      // path: merge while the test is live (protects subscription-
      // pushed vulns the paginated fetch hasn't seen yet), replace
      // once it's terminal (BE is authoritative — clears stale local-
      // only vulns from cached state). Without the terminal-replace
      // behavior a BE-side dedup wouldn't be reflected here either.
      const sorter = new VulnerabilityOperationsClass();
      if (vulns && vulns.length > 0) {
        let nextVulns: any[];
        if (this.isLive) {
          const incomingIds = new Set(
            vulns.map((v: any) => v?.id).filter(Boolean),
          );
          const localOnly = this.vulns.filter(
            (v) => v.id && !incomingIds.has(v.id),
          );
          nextVulns = [...vulns, ...localOnly];
        } else {
          nextVulns = vulns;
        }
        this.vulns = sorter.sortHighestRisk(nextVulns);
      } else if (this.isLive) {
        // Backend returned nothing on a live test — keep whatever the
        // subscription has given us locally. Re-sort so any newly-
        // merged entries settle into the canonical order.
        this.vulns = sorter.sortHighestRisk(this.vulns);
      } else {
        // Terminal test with no BE response — authoritative empty.
        this.vulns = [];
      }
    } // Else should not have been called
  }

  // Get Display Vulns
  // ------------------
  // The findings to render for this test, drawn from BOTH places they can
  // live:
  //
  //   - `vulns` — records fetched from the backend. Not a terminal-test-only
  //     concern: the pentest container writes each finding to DynamoDB
  //     *before* the discovery event is published (queuetriggers.py — "It
  //     has already saved the vuln to the database"), and the TestResults.vulns
  //     resolver queries tenant-testid-index with no terminal-status gate.
  //     A mid-run fetch therefore returns everything found so far.
  //   - `liveFindings` — slim rows streamed by the discovery subscription
  //     during THIS browser session. The only source for findings that
  //     landed after the most recent fetch.
  //
  // Neither is sufficient alone, which is what made in-flight findings so
  // fragile. liveFindings is session memory: a page refresh or a re-login
  // empties it, so a run whose findings had only ever arrived by
  // subscription rendered an empty table — and a test started before
  // logging out came back showing nothing at all. `vulns` is a snapshot as
  // of the last fetch and misses anything found since.
  //
  // The union is keyed on id with the hydrated record winning: it carries
  // the screenshot, sub-findings and reproduction steps that the slim live
  // row doesn't have.
  //
  // A terminal test stops unioning ONCE ITS FINAL RESULT HAS ACTUALLY BEEN
  // FETCHED — terminalHydrationComplete, not merely a terminal testStatus.
  // The backend is authoritative once a test ends (see the merge-vs-replace
  // note in setFullTestResult), so a row it has since removed in a dedup
  // cleanup must not be resurrected from stale session memory — but it can
  // only be authoritative once we have actually heard from it.
  //
  // Gating on status alone was a data-loss bug: the status flips the instant
  // the terminal event arrives, while the fetch that fills `vulns` is a
  // separate, later, failure-prone round trip. In the window between the two
  // — and forever, if that fetch failed — the union was off and `vulns` was
  // empty, so a run that had streamed dozens of findings rendered as zero.
  // Findings the user watched arrive do not disappear because a test ended.
  //
  // Whatever the union produces is passed through the taxonomy guard before
  // it is returned, so a finding carrying a severity this build cannot place
  // is dropped here — once, for every surface — rather than appearing in the
  // table while being absent from every tile and filter. See
  // utils/vulnTaxonomy.
  getDisplayVulns(): VulnerabilityClass[] {
    const hydratedVulns = this.vulns ?? [];

    const isInFlight =
      !!this.testStatus && IN_FLIGHT_TEST_STATUSES.has(this.testStatus);
    const backendHasSpokenFinally = this.terminalHydrationComplete;
    if ((!isInFlight && backendHasSpokenFinally) || !this.liveFindings?.length) {
      return selectDisplayableFindings(hydratedVulns, `test ${this.id}`);
    }

    const hydratedIds = new Set(
      hydratedVulns.map((vuln) => vuln.id).filter(Boolean)
    );
    const liveOnlyFindings = this.liveFindings
      .filter((finding) => finding.id && !hydratedIds.has(finding.id))
      .map((finding) => VulnerabilityClass.createFromSubscription(finding));

    if (liveOnlyFindings.length === 0) {
      return selectDisplayableFindings(hydratedVulns, `test ${this.id}`);
    }
    return selectDisplayableFindings(
      [...hydratedVulns, ...liveOnlyFindings],
      `test ${this.id}`,
    );
  }

  // Subscribe To Vulns
  // -------------------
  // Assume logged in - otherwise how did we get here?
  // Only call after test id has been returned - otherwise we will return with no subscripton
  //
  // Subscribe to the slim live-discovery stream (onVulnerabilityDiscovered)
  // while this test is in-flight, then reconcile once against the backend for
  // everything found before the stream existed. Streamed events append to
  // liveFindings; the reconcile populates `vulns`. Surfaces read the two
  // together via getDisplayVulns() — the stream alone is session memory and
  // does not survive a refresh.
  //
  // The dashboard cards are unaffected either way: they stay on the last
  // completed test for the duration of the run.
  async subscribeToVulns(
    subscriptionClient: any,
    webappid: string
  ): Promise<void> {
    if (!this.id || !this.isLive) return;

    // Record the intent before the guards below: a caller asking for the
    // stream while one is already open still means "keep this open".
    this.vulnStreamDesired = true;

    if (this.subscriptionToVulns || this.isSubscribingToVulns) return;
    this.isSubscribingToVulns = true;

    try {
      await this.openVulnDiscoveryStream(subscriptionClient, webappid);
    } finally {
      this.isSubscribingToVulns = false;
    }
  }

  // Open Vuln Discovery Stream
  // ---------------------------
  // The body of subscribeToVulns, split out so the re-entrancy flag above
  // is released on every exit path without wrapping the whole method in a
  // try/finally.
  private async openVulnDiscoveryStream(
    subscriptionClient: any,
    webappid: string
  ): Promise<void> {
    this.subscriptionToVulns = await subscribeToVulnerabilityDiscovered({
      subscriptionClient,
      webappid: webappid,
      onNext: (msg: SubscriptionMessage) => {
        try {
          const eventData = msg?.onVulnerabilityDiscovered;
          if (!eventData?.id) return;

          const alreadyExists = this.liveFindings.some(
            (finding) => finding.id === eventData.id
          );
          if (!alreadyExists) {
            // LIVE_FINDING_OBSERVED_AT — when this vuln was last seen.
            //
            // The discovery event carries dateFirstFound but no
            // dateLastSeen (VulnerabilityDiscoveryEvent has no such field),
            // so without this the "Last Seen" column collapsed to the
            // em-dash placeholder for every finding of an in-flight test —
            // even though the running test is, by definition, seeing it
            // right now. Falling back to dateFirstFound the way the report
            // generator does would be wrong here: on a carried-forward vuln
            // dateFirstFound is the ORIGINAL observation date (weeks old),
            // which is precisely the value "Last Seen" exists to contrast
            // against.
            //
            // The event's arrival IS the observation, so we stamp receipt
            // time. It differs from the backend's own dateLastSeen (written
            // at row save) by the publish/delivery latency — seconds — and
            // both date columns render at day granularity. Once the test
            // terminates these slim rows are replaced wholesale by the
            // hydrated backend vulns, which carry the authoritative value.
            const observedAtEpochSeconds = Math.floor(
              Date.now() / EPOCH_TO_JS_DATE_CONVERSION
            );
            this.liveFindings.push(
              toLiveFinding(eventData, observedAtEpochSeconds)
            );
            eventBus.emit(LIVE_FINDINGS_UPDATE, {
              webappid,
              testid: this.id,
            });
          }

          // Self-heal testStatus: a finding arriving means the test is past
          // Provisioning. The backend may not always publish the intermediate
          // Running status (e.g. for short tests that fail during provisioning
          // and surface the failure as a finding — the ServerUnreachable case).
          // The finding itself proves the test has progressed past the
          // "Starting…" phase, so we advance the local status and notify.
          if (this.testStatus === TEST_STATUS_PROVISIONING) {
            this.testStatus = TEST_STATUS_RUNNING;
            eventBus.emit(TEST_STATUS_UPDATE, {
              webappid,
              testid: this.id,
            });
          }
        } catch (err) {
          console.error(
            `Error processing live finding for test ${this.id}:`,
            err
          );
        }
      },
      onError: (subscriptionError: any) => {
        // An error frame is terminal for the subscription: AppSync has
        // already stopped delivering on it and the client has dropped its
        // registration. Release the handle so this stream can be opened
        // again — the in-flight resync on WebApplicationClass calls
        // subscribeToVulns on every tick and its guard keys off exactly
        // this field. Leaving it set is what used to make one rejected
        // subscription permanent for the rest of the session.
        console.error(
          `Vuln discovery subscription error for test ${this.id}:`,
          subscriptionError
        );
        this.subscriptionToVulns = null;
        this.disposeReconnectListener();
      },
    });

    // Close the gap left by every WebSocket reconnect. The client restarts
    // the subscription itself, but AppSync has no replay: findings
    // published while the socket was down were never delivered to anyone
    // and would otherwise stay invisible until the test terminated. This
    // is the direct cause of a long-running test showing a stale finding
    // count for hours after a laptop sleep.
    this.disposeReconnectListener();
    this.reconnectListenerDisposer =
      subscriptionClient?.onReconnected?.(() => {
        this.reconcileLiveFindings(webappid).catch(() => {
          // reconcileLiveFindings already logs; the periodic resync
          // retries within INFLIGHT_RESYNC_INTERVAL_MS regardless.
        });
      }) ?? null;

    // Catch-up reconcile — everything found BEFORE this stream existed.
    //
    // The subscription only delivers findings published from this moment
    // on. On a fresh page load or a re-login mid-run, every finding the
    // test produced up to now was published to a socket that no longer
    // has a listener; without this fetch the table would show only what
    // arrives from here on, which is what made a refresh look like the
    // findings had been lost.
    //
    // Deliberately runs AFTER the subscribe resolves rather than before:
    // anything published while the fetch is in flight still lands via the
    // now-live stream, so the two sources overlap instead of leaving a
    // window. Duplicates are harmless — getDisplayVulns() dedupes on id
    // and setFullTestResult merges (rather than replaces) while live.
    //
    // Which catch-up depends on what this instance already holds. A test
    // that has never been hydrated needs the full record — that is the
    // fresh-page-load / re-login case, and it is also what gives the vuln
    // detail page its screenshots and reproduction steps. A test that HAS
    // been hydrated only needs the slim delta, and taking it matters:
    // this method now runs again whenever the resync re-opens a dropped
    // stream, and re-pulling every finding in full on each of those would
    // be a heavy price for a fallback path.
    try {
      if (this.fullResultLoaded) {
        await this.reconcileLiveFindings(webappid);
      } else {
        await this.getFullTestResult(this.id);
        eventBus.emit(LIVE_FINDINGS_UPDATE, {
          webappid,
          testid: this.id,
        });
      }
    } catch (catchUpError) {
      // Non-fatal: the live stream is already established, so new
      // findings still arrive. Only the pre-subscription backlog is
      // missing, and it fills in when the test terminates and the full
      // result hydrates.
      console.error(
        `Error loading findings-so-far for live test ${this.id}:`,
        catchUpError
      );
    }
  }

  // Reconcile Live Findings
  // ------------------------
  // Pull the findings the backend has recorded for this in-flight test and
  // merge anything the discovery stream hasn't delivered into liveFindings.
  //
  // The stream is best-effort. AppSync has no replay, so every finding
  // published while the socket was down is lost to this session — which is
  // exactly how a multi-hour run came back from a laptop sleep still
  // showing the three findings it had before. The rows were in DynamoDB
  // the whole time; nothing was going to go and read them. This is that
  // read, driven by three triggers: the in-flight resync poll, the tab
  // becoming visible again, and every successful WebSocket reconnect.
  //
  // Deliberately reads the slim projection rather than getFullTestResult:
  // the full query returns every finding with its sub-findings, captured
  // HTTP request/response bodies and screenshot field — far too heavy to
  // poll. getDisplayVulns() unions liveFindings over `vulns` for an
  // in-flight test, so slim rows render the table correctly; the hydrated
  // records land when the test terminates.
  async reconcileLiveFindings(webappid: string): Promise<void> {
    if (!this.id || !this.isLive) return;

    try {
      const findings = await fetchLiveFindingsSnapshot(this.id);
      if (!findings.length) return;

      // Anything already hydrated into `vulns` is not missing —
      // getDisplayVulns prefers those richer records, so adding slim
      // copies would be pure noise.
      const knownIds = new Set<string>(
        this.liveFindings.map((finding) => finding.id)
      );
      this.vulns.forEach((vuln) => {
        if (vuln.id) knownIds.add(vuln.id);
      });

      const missingFindings = findings.filter(
        (finding) => finding.id && !knownIds.has(finding.id)
      );
      if (!missingFindings.length) return;

      // dateLastSeen comes off the stored row here, unlike the streamed
      // path which has to stamp receipt time.
      this.liveFindings.push(
        ...missingFindings.map((finding) =>
          toLiveFinding(finding, finding.dateLastSeen ?? 0)
        )
      );

      eventBus.emit(LIVE_FINDINGS_UPDATE, {
        webappid,
        testid: this.id,
      });
    } catch (reconcileError) {
      // Non-fatal by design: this IS the fallback path. The next tick
      // retries, and the terminal-transition hydrate is authoritative.
      console.error(
        `Error reconciling live findings for test ${this.id}:`,
        reconcileError
      );
    }
  }

  // Did a consumer ask for the discovery stream and then lose it? True
  // only for a stream that was wanted and has since gone — a subscription
  // AppSync rejected, or one lost with a socket that never came back.
  // The resync path uses this to re-open exactly those.
  needsVulnStreamReopen(): boolean {
    return this.vulnStreamDesired && !this.subscriptionToVulns;
  }

  // Unsubscribe From Vulns
  // -----------------------
  //
  async unsubscribeFromVulns(): Promise<void> {
    this.vulnStreamDesired = false;
    this.subscriptionToVulns?.unsubscribe?.();
    this.subscriptionToVulns = null;
    this.disposeReconnectListener();
  }

  // Detach the reconnect listener from the shared subscription client.
  // Safe to call when none is registered.
  private disposeReconnectListener(): void {
    this.reconnectListenerDisposer?.();
    this.reconnectListenerDisposer = null;
  }
}

export default TestResultsClass;
